"use client"
import { useEffect } from "react"

import {
  getNearByPropertiesABTestVariant,
  NearByPropertiesABTestVariants,
} from "@/app/src/hooks/useNearByPropertiesABTesting"
import { PDPPageOrigins } from "@/constant"
import {
  FeaturesByCategoryType,
  PropertyHighlightType,
  PropertyTypeV2,
  StructuredDescriptionType,
} from "commonModule/services/common"
import {
  registerViewDetailPageEventMixpanel,
  selectBookingStore,
  selectTotalExcludingTaxes,
} from "stores/booking/bookingSlice"
import { useAppDispatch, useAppSelector } from "stores/hooks"

type UseTrackPdpViewArgs = {
  property: PropertyTypeV2
  amenities: FeaturesByCategoryType[]
  propertyhighlights?: PropertyHighlightType[]
  structuredDescription?: StructuredDescriptionType | null
  isAISummary?: boolean
  multiUnitGrpId?: string | null
}

export const useTrackPdpView = ({
  property,
  amenities,
  propertyhighlights,
  structuredDescription,
  isAISummary = false,
  multiUnitGrpId,
}: UseTrackPdpViewArgs) => {
  const dispatch = useAppDispatch()
  const bookingStore = useAppSelector(selectBookingStore)
  const totalExcludingTaxes = useAppSelector(selectTotalExcludingTaxes)

  useEffect(() => {
    if (!bookingStore.isLoading && bookingStore.isPreqouteLoaded) {
      const search = window.location.search
      const params = new URLSearchParams(search)
      const actualPrice = params?.get("searchPrice") || ""
      const savedPrice = params?.get("savedPrice") || ""
      const strikePrice = params?.get("strikePrice") || ""
      const source = params?.get("source") || ""
      const fromMultiUnit = params?.get("fromMultiUnit") || null
      const crossSellVersion =
        getNearByPropertiesABTestVariant() ===
        NearByPropertiesABTestVariants.V0_VERSION
          ? "Old"
          : "New"
      const displayedDescriptionVariant =
        structuredDescription?.combined_description ? "structured" : "original"
      const featured = property?.featuredUnit || false
      const isReferrerSearchPage =
        document?.referrer?.includes("vacation-rentals")
      const amenityKeywords = amenities.flatMap(
        (amenityCategory) => amenityCategory.keywords || [],
      )
      const hasAirbnbAmenitiesEnhanced = amenityKeywords.some(
        (amenity) => !!amenity.key_description,
      )
      const hasSelfCheckInAmenity = amenityKeywords.some((amenity) =>
        ["self check-in", "self check-in via keypad"].includes(
          amenity.key_name?.trim().toLowerCase(),
        ),
      )
      void dispatch(
        registerViewDetailPageEventMixpanel({
          property_id: property?.property_id,
          city: property?.city,
          state: property?.state,
          country: property?.country,
          excludingTaxesTotalAmount: !bookingStore.isDateAvailable
            ? property?.min_price || 0
            : totalExcludingTaxes,
          max_price: property.max_price,
          min_price: property.min_price,
          no_of_bedrooms: property.no_of_bedrooms,
          property_type_desc: property.property_type_desc,
          property_rating_star_sum: property.property_rating_star_sum,
          property_rating_user_count: property.property_rating_user_count,
          description_variant: displayedDescriptionVariant,
          actual_price: actualPrice,
          saved_price: savedPrice,
          strike_price: strikePrice,
          featured,
          aiSummaryShown: isAISummary,
          savingsAmountBasedOnPrecisionPricing: "NA",
          source: isReferrerSearchPage
            ? PDPPageOrigins[source as keyof typeof PDPPageOrigins] || null
            : null,
          highlightsShown:
            (propertyhighlights && propertyhighlights?.length > 0) || false,
          airbnbAmenitiesEnhanced: hasAirbnbAmenitiesEnhanced,
          selfCheckInAvailable: hasSelfCheckInAmenity ? "Yes" : "No",
          isMultiUnit: !!multiUnitGrpId,
          muPDPFlow: fromMultiUnit ? "via_mu_detail" : "direct_to_pdp",
          crossSellVersion,
          preferredHost: property?.preferredHostBadge || false,
        }),
      )
    }
  }, [
    property?.preferredHostBadge,
    bookingStore.isDateAvailable,
    bookingStore.isLoading,
    bookingStore.isPreqouteLoaded,
    dispatch,
    property?.city,
    property?.country,
    property.max_price,
    property.min_price,
    property.no_of_bedrooms,
    property?.property_id,
    property.property_rating_star_sum,
    property.property_rating_user_count,
    property.property_type_desc,
    property?.state,
    structuredDescription?.combined_description,
    totalExcludingTaxes,
    propertyhighlights,
    amenities,
    isAISummary,
    multiUnitGrpId,
    property?.featuredUnit,
  ])

  useEffect(() => {
    void (async () => {
      const mixpanelV1Client = (await import("services/mixpanel"))
        .mixpanelV1Client
      mixpanelV1Client.VIEW_PROPERTY_DETAIL_SCREEN({
        propertyId: property?.property_id,
        propertyName: property?.property_name,
        bathrooms: property?.no_of_bathrooms,
        bedrooms: property?.no_of_bedrooms,
      })
    })()
  }, [
    property?.no_of_bathrooms,
    property?.no_of_bedrooms,
    property?.property_id,
    property?.property_name,
  ])
}
